import {AlertPayload} from 'Core/ducks/alerts';
import {ModalState, showModal} from 'Core/ducks/modal';
import {AsyncPage, fromResult, ServerError, toPageEither} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {sequenceT} from 'fp-ts/lib/Apply';
import * as C from 'fp-ts/lib/Console';
import * as E from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import {NonEmptyArray} from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {AnyAction, Dispatch} from 'redux';
import {doNothing} from 'Shared/utilities/IOUtilities';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';
import {fromLocalStorage, LocalStorageKey, toLocalStorage} from 'Shared/utilities/localStorage';
import {parseBoolean} from 'Shared/utilities/parsers';
import {prop} from 'Shared/utilities/prop';
import {setSelectedAgency, setUserClaims} from 'Users/ducks/userProfile';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {AgentClaims} from 'Users/models/AgentClaims';
import {UserClaims, UserTypeClaimKey, UserTypeClaims, EnrollTrackClaims} from 'Users/models/Claims';

export const handleAlerts = (userAlertsAsync: AsyncPage<Page<AlertPayload>>, onShowModal: typeof showModal) => {
  const haveAlertsBeenDismissed = pipe(
    fromLocalStorage(LocalStorageKey.AlertsDismissed),
    IO.map(O.fromNullable),
    IO.map(O.map(parseBoolean))
  );
  const isTruthy = (option: O.Option<boolean>) => O.isSome(option) && option.value;

  return pipe(
    userAlertsAsync,
    toPageEither,
    E.chain(
      E.fromPredicate(
        alerts => alerts.totalCount > 0 && !isTruthy(haveAlertsBeenDismissed()),
        () => new ServerError() as unknown
      )
    ),
    E.fold(doNothing, alerts => () =>
      onShowModal(
        ModalState.Alerts({
          messages: alerts.result,
          onDismissAlerts: toLocalStorage(LocalStorageKey.AlertsDismissed)(true),
        })
      )
    )
  );
};

const selectUserDefinedDefaultOrFirstAgency = (agencies: NonEmptyArray<AgentAgencyClaim>) =>
  pipe(
    agencies,
    NEA.filter(agency => agency.isDefault),
    O.map(NEA.head),
    O.getOrElse(() => NEA.head(agencies))
  );

export const defaultAgency = flow(
  (claims: AgentClaims) =>
    O.getFirstMonoid<AgentClaims>().concat(O.fromNullable(claims.impersonatedAgentClaim), O.some(claims)),
  O.map(prop('agencyAssociations')),
  O.chain(NEA.fromArray),
  O.map(selectUserDefinedDefaultOrFirstAgency),
  O.map(agency => {
    const isNewYork = agency.isDefault ? agency.isDefaultShowNewYork : getIsNewYorkFromAgency(O.some(agency));

    return setSelectedAgency(O.some(agency), isNewYork);
  })
);

export const enrollTrackAgentDefaultAgency = flow(
  (claims: EnrollTrackClaims) =>
    claims.impersonatedEnrollTrackClaim
      ? O.fromNullable(claims.impersonatedEnrollTrackClaim.agencyAssociations)
      : O.none,
  O.chain(NEA.fromArray),
  O.map(selectUserDefinedDefaultOrFirstAgency),
  O.map(agency => {
    const isNewYork = agency.isDefault ? agency.isDefaultShowNewYork : getIsNewYorkFromAgency(O.some(agency));

    return setSelectedAgency(O.some(agency), isNewYork);
  })
);

const dispatchAction = (dispatch: Dispatch) => (actionCreator: AnyAction) => {
  dispatch(actionCreator);
};

export const loadClaimsAndProfile = (dispatch: Dispatch) => (userClaims: UserClaims) => {
  const setAgency = userClaims.userTypes.map(
    UserTypeClaims.match({
      [UserTypeClaimKey.AgentSite]: flow(defaultAgency, IO.of, IO.map(O.fold(() => {}, dispatchAction(dispatch)))),

      [UserTypeClaimKey.EnrollTrack]: flow(
        enrollTrackAgentDefaultAgency,
        IO.of,
        IO.map(O.fold(() => {}, dispatchAction(dispatch)))
      ),

      default: userType => C.log(`Could not parse user type: ${userType}`),
    })
  );
  const setClaims = () => pipe(fromResult(userClaims), setUserClaims, dispatchAction(dispatch));

  return sequenceT(IO.io)(setClaims, ...setAgency);
};
